import type { AnalyticsEventId, EventPayloadContent } from "@wayflyer/flyui";
import { Button, GoogleButton, Tooltip, useTheme } from "@wayflyer/flyui";
import { IconCheck16Solid as CheckIcon } from "@wayflyer/flyui-icons/16/solid";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { styled } from "styled-components";

import type { FormSpec as FormProps } from "../connector-content/interface";
import { useConnector } from "../connector-context";
import type { ConnectorModalProps } from "../connector-modal";

import { ConnectorFullLogo, ConnectorFullLogoButton, ConnectorFullLogoButtonWithHover } from "./connector-full-logo";
import {
  ConnectorLogo,
  ConnectorLogoButton,
  ConnectorLogoButtonWithHover,
  type ConnectorLogoSize,
} from "./connector-logo";

export type ConnectorState = "COMING_SOON" | "NOT_CONNECTED" | "CONNECTED" | "DISABLED" | "DISCONNECTED";

const ConnectorDisabled = {
  COMING_SOON: "COMING_SOON",
  CONNECTED: "CONNECTED",
  DISABLED: "DISABLED",
};

export type ConnectorMode = "Logo" | "FullLogo" | "Button" | "Google";

export interface ConnectorProps {
  name: string;
  slug: string;
  state: ConnectorState;
  title?: string;
  formFields?: FormProps;
  link?: ConnectorModalProps["link"];
  category: string;
  connectClickHandler: (data: any) => any;
  size?: ConnectorLogoSize;
  mode?: ConnectorMode;
  showRutterDisclaimer?: boolean;
  showCodatDisclaimer?: boolean;
  disabledReason?: string;
}

const LogoWithTooltip = ({ logo, disabledReason }: { logo: ReactNode; disabledReason?: string }) => {
  const intl = useIntl();

  return (
    <Tooltip
      content={
        disabledReason
          ? disabledReason
          : intl.formatMessage({
              id: "shared-ui: connector not available",
              defaultMessage: "Not available, please contact support",
            })
      }
    >
      {logo}
    </Tooltip>
  );
};

const ButtonContainer = styled.div`
  position: relative;
`;

const LogoButtonContainer = styled(ButtonContainer)`
  &:not(:last-child) {
    margin-right: 2%;
  }
`;

const CenteredBadge = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 21px;
  height: 21px;
  border-radius: var(--sizes-radius-full);
  background-color: var(--palette-content-status-positive);
  right: -7px;
  top: -7px;
  position: absolute;
  text-align: center;
  line-height: 19px;
  z-index: 100;
`;

export const Connector = ({ connectClickHandler, size, mode = "Logo", ...connector }: ConnectorProps) => {
  const { name, slug, state, title, formFields, link, showRutterDisclaimer, showCodatDisclaimer, disabledReason } =
    connector;
  const theme = useTheme();
  const intl = useIntl();
  const { openConnectorModal, updateConnectorModalFields } = useConnector();
  const isConnected = state === "CONNECTED";
  const isNotConnected = state === "NOT_CONNECTED";
  const isDisconnected = state === "DISCONNECTED";
  const isComingSoon = state === "COMING_SOON";
  const isDisabled = isComingSoon || state === "DISABLED";
  const [isSubmitting, setIsSubmitting] = useState(false);

  const connectedBadge = (
    <CenteredBadge>
      <CheckIcon color={theme.palette.content.neutral.onInverse} />
    </CenteredBadge>
  );

  const handleModalSubmit = useCallback(
    (formData) => {
      setIsSubmitting(true);
      connectClickHandler(formData);
    },
    [connectClickHandler]
  );

  const handleClickHandler = (data: any): any => {
    setIsSubmitting(true);
    connectClickHandler(data);
  };

  const renderModal = useCallback(() => {
    openConnectorModal(name, handleModalSubmit, title, formFields, link, showRutterDisclaimer, showCodatDisclaimer);
  }, [formFields, link, handleModalSubmit, name, openConnectorModal, title, showRutterDisclaimer, showCodatDisclaimer]);

  const buttonProps = {
    id: `connect-${name}`,
    "data-testid": `connect-${slug}`,
    disabled: isDisabled || isSubmitting,
    ...(isConnected && {
      style: { border: `2px solid ${theme.palette.content.status.positive}` },
    }),
    ...((isDisconnected || isNotConnected) && {
      onClick: formFields ? renderModal : handleClickHandler,
    }),
    ...(!isDisabled && {
      title: name,
    }),
  };

  const buttonAnalyticsAttrs: EventPayloadContent[AnalyticsEventId.click] = {
    type: "button",
    buttonType: "connect",
    connectSlug: slug,
  };

  useEffect(
    () => formFields && updateConnectorModalFields(name, formFields, showRutterDisclaimer, showCodatDisclaimer),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formFields]
  );

  switch (mode) {
    case "Logo":
      // eslint-disable-next-line no-case-declarations
      const LogoButtonComponent = isNotConnected ? ConnectorLogoButtonWithHover : ConnectorLogoButton;
      // eslint-disable-next-line no-case-declarations
      const logo = (
        <LogoButtonContainer key={name}>
          <LogoButtonComponent {...buttonProps} analyticsAttrs={buttonAnalyticsAttrs}>
            <>
              {isConnected ? connectedBadge : null}
              <ConnectorLogo size={size} connectorName={name} alt={name} ariaLabel={name} key={name} />
            </>
          </LogoButtonComponent>
        </LogoButtonContainer>
      );

      return isDisabled ? <LogoWithTooltip logo={logo} disabledReason={disabledReason} /> : logo;
    case "FullLogo":
      // eslint-disable-next-line no-case-declarations
      const FullLogoButtonComponent = isNotConnected ? ConnectorFullLogoButtonWithHover : ConnectorFullLogoButton;
      // eslint-disable-next-line no-case-declarations
      const fullLogo = (
        <ButtonContainer key={name}>
          <FullLogoButtonComponent {...buttonProps} analyticsAttrs={buttonAnalyticsAttrs}>
            <>
              {isConnected ? connectedBadge : null}
              <ConnectorFullLogo connectorName={name} alt={name} ariaLabel={name} key={name} />
            </>
          </FullLogoButtonComponent>
        </ButtonContainer>
      );
      return isDisabled ? <LogoWithTooltip logo={fullLogo} disabledReason={disabledReason} /> : fullLogo;
    case "Google":
      return (
        <GoogleButton
          {...buttonProps}
          label={intl.formatMessage({ id: "shared-ui: Connect with Google", defaultMessage: "Connect with Google" })}
          analyticsAttrs={buttonAnalyticsAttrs}
        />
      );
    default:
      return (
        <ButtonContainer>
          <Button
            variant="Primary"
            onClick={buttonProps.onClick}
            size="small"
            disabled={buttonProps.disabled || ConnectorDisabled[state]}
            analyticsAttrs={buttonAnalyticsAttrs}
          >
            {intl.formatMessage({ id: "shared-ui: connect button", defaultMessage: "Connect" })}
          </Button>
        </ButtonContainer>
      );
  }
};
